/** What `Money` looks like — a test and a server assertion read it. */
export const MONEY_PATTERN = /^-?\d+(\.\d{2})?$/;
/**
 * Which way a difference went, and so its colour (§3.2): `over` amber,
 * `under` green, `zero` a faint *—*.
 *
 * For a **margin** the tone is read the cost way round: margin *down* is
 * `over` (amber), margin *up* is `under` (green) — §3.2's *margin points amber
 * when down, green when up*.
 */
export const DELTA_TONES = ['over', 'under', 'zero'];
/**
 * When the yard's morning happens — §4.5: *Decide in the morning* is filled by
 * morning assignment at 6:30 (*picked at 6:30*, *the yard picks one at 6:30*,
 * Materials' *Load at the yard · 6:30 AM*). One number for the page, in the
 * branch's zone; a branch setting is a question for Aaron.
 */
export const YARD_MORNING_AT = '06:30';
/** Whether a PO went to the vendor or was noted by the office while purchasing is off (§27.2). */
export const PO_ENTRIES = ['sent', 'noted'];
// ---------------------------------------------------------------------------
// The visit and its days (§3.1, §17.1)
// ---------------------------------------------------------------------------
/**
 * A visit's status — §17.1, the chip in the header and the mark's colour.
 * Words: *Unscheduled · Scheduled · In progress · Completed · Closed · On hold
 * · Cancelled · Skipped*. Marks: `unscheduled` grey with the band *TBD* and a
 * dash; `scheduled` amber; `in_progress` teal; `completed` and `closed` green
 * (`closed` with the lock); `on_hold` warn; `cancelled` grey; `skipped` grey
 * with the strike across the day.
 *
 * Not `VisitStatus` (`state-machines.ts`), which is a **day's** status.
 */
export const VISIT_PAGE_STATUSES = [
    'unscheduled', 'scheduled', 'in_progress', 'completed', 'closed', 'on_hold', 'cancelled', 'skipped',
];
/**
 * How the visit bills (§4.1 `work_items.billing_kind`) — the header's third
 * tag: *Fixed price · Time & materials · Per visit*.
 */
export const VISIT_BILLING_KINDS = ['fixed', 'time_materials', 'per_visit'];
// ---------------------------------------------------------------------------
// Materials (§4.3, §8.1, §8.2, do-not-substitute 6)
// ---------------------------------------------------------------------------
/**
 * Where a material comes from — do-not-substitute 6, in these words:
 * `yard` *From the yard* · `order_site` *Order · delivered to the site* ·
 * `order_yard` *Order · delivered to the yard* · `order_pickup` *Order · crew
 * picks up* · `field` *Bought in the field*.
 */
export const MATERIAL_SOURCES = ['yard', 'order_site', 'order_yard', 'order_pickup', 'field'];
/**
 * Where a material stands — §8.2, `visit_material_state()` (§22 test 6), in
 * these words and tones: `to_order` *To order* (warn) · `ordered` *Ordered*
 * (blue) · `ready_pickup` *Ready for pickup* (indigo) · `at_yard` *At the
 * yard* (green) · `on_site` *Delivered to site* (green) · `loaded` *Loaded*
 * (green) · `bought` *Bought* (purple) · `used` *Used* (slate). Never free
 * text, never a percentage.
 */
export const MATERIAL_STATES = [
    'to_order', 'ordered', 'ready_pickup', 'at_yard', 'on_site', 'loaded', 'bought', 'used',
];
/**
 * By status's groups (§8.2), in order, drawn only when not empty:
 * *Needs ordering · On the way · Ready · Bought in the field · Used*.
 */
export const MATERIAL_GROUPS = ['needs_ordering', 'on_the_way', 'ready', 'bought', 'used'];
/** Which group each state files under (§8.2's last column). */
export const MATERIAL_STATE_GROUP = {
    to_order: 'needs_ordering',
    ordered: 'on_the_way',
    ready_pickup: 'on_the_way',
    at_yard: 'ready',
    on_site: 'ready',
    loaded: 'ready',
    bought: 'bought',
    used: 'used',
};
/**
 * Why a swap (§4.3 `swap_reason`): *Backordered · Out of stock · Customer
 * asked · Better fit · Other*.
 */
export const SWAP_REASONS = ['backordered', 'out_of_stock', 'customer_asked', 'better_fit', 'other'];
// ---------------------------------------------------------------------------
// Vendor rentals (§4.6, §10.2)
// ---------------------------------------------------------------------------
/**
 * A vendor rental's state — §10.2: `to_book` *To book* (warn) · `booked`
 * *Booked* (blue) · `picked_up` *Picked up* (indigo) · `returned` *Returned*
 * (green) · `cancelled` *Cancelled* (grey).
 */
export const RENTAL_STATES = ['to_book', 'booked', 'picked_up', 'returned', 'cancelled'];
/** How a rental gets there (§4.6 `how`): *Crew picks up · Vendor delivers to the site*. */
export const RENTAL_HOWS = ['pickup', 'delivery'];
// ---------------------------------------------------------------------------
// Receipts (§4.7, §12)
// ---------------------------------------------------------------------------
/** A receipt's review — §12: *To review* (warn) · *Approved* (green) · *Rejected* (grey). */
export const RECEIPT_STATES = ['to_review', 'approved', 'rejected'];
/** How a receipt was paid (§4.7 `paid_with`): *Company card · Own money · Account*. */
export const PAID_WITH = ['company_card', 'own_money', 'account'];
/** A receipt's category (§4.7). */
export const RECEIPT_CATEGORIES = ['materials', 'fees', 'tools_supplies', 'fuel', 'other'];
// ---------------------------------------------------------------------------
// From the crew (§4.8, §14)
// ---------------------------------------------------------------------------
/** A crew photo's tag (§4.8): *Before · During · Issue · Finished*. */
export const PHOTO_TAGS = ['before', 'during', 'issue', 'finished'];
/** Where a crew note lives (§13.1) — the source chip: *This visit · Property · Contact · Job*. */
export const CREW_NOTE_SOURCES = ['visit', 'property', 'contact', 'job'];
/**
 * How a close-out entered by the office reached it (§27.2 `reached_by`):
 * *Call · Text · Paper sheet · In person*.
 */
export const REACHED_BY = ['call', 'text', 'paper', 'in_person'];
// ---------------------------------------------------------------------------
// Money categories (§6)
// ---------------------------------------------------------------------------
/**
 * The cost categories of §6, in order: *Labor · Materials · Company equipment
 * · Rented from a vendor · Fees · (Subcontractors)* — the last drawn only when
 * there are any.
 */
export const COST_CATEGORIES = ['labor', 'materials', 'equipment', 'rental', 'fees', 'subs'];
//# sourceMappingURL=values.js.map